<template>
  <div class="perfis-step-planos">
    <PerfisInfoAlert>
      Nenhum plano selecionado significa <strong>Sem restrição</strong> (todas
      as formas LifeGrowth da loja permanecem disponíveis para os usuários deste
      perfil).
    </PerfisInfoAlert>

    <PerfisBulkSelectActions
      :disabled="!idperfilEdicao"
      :loading-select-all="loadingSelectAll"
      @select-all="selectAll"
      @clear-all="clearAll"
    />

    <p class="text-end mb-2 perfis-selection-hint">
      <v-icon small color="primario" class="mr-1">fas fa-credit-card</v-icon>
      <small>{{ selectedCount }} plano(s) selecionado(s) no perfil</small>
    </p>

    <DataTable
      ref="datatable"
      :headers="headers"
      :endpoint="endpoint"
      :filters="filters"
      search
      searchLabel="Buscar descrição ou ID externo"
      noHideFilters
      showSelect
      sortBy="descricao"
      :sortDesc="false"
      :itemsPerPage="15"
      :limitPage="[10, 15, 25, 50]"
      :auto-search="false"
      classe="elevation-0"
      @select="onTableSelect"
      @set-value-filter="onFiltersApplied"
    >
      <template v-slot:tipo="{ col }">
        <Chip
          v-if="col"
          color="cyan"
          :small="true"
          dense
          rounded
          :text="col"
          icon="fas fa-tag"
          :icon-size="14"
        />
        <span v-else class="grey--text">—</span>
      </template>
      <template v-slot:id_formapagto_erp="{ col }">
        <Chip
          v-if="col"
          color="grey"
          :small="true"
          dense
          rounded
          :text="String(col)"
          icon="fas fa-barcode"
          :icon-size="14"
        />
        <span v-else class="grey--text">—</span>
      </template>
    </DataTable>
  </div>
</template>

<script>
import DataTable from "@/components/DataTable";
import Chip from "@/components/Chip";
import {
  PERFIL_USUARIO_DESVINCULAR_TODOS_PLANOS,
  PERFIL_USUARIO_PLANOS_LIST,
  PERFIL_USUARIO_VINCULAR_TODOS_PLANOS
} from "@/store/actions/perfilUsuario";
import PerfisInfoAlert from "./PerfisInfoAlert";
import PerfisBulkSelectActions from "./PerfisBulkSelectActions";
import { readDatatableFilterMap } from "./perfis-datatable-filters";
import { mergeCrossPageSelection, sameIdSet, uniqueIds } from "./utils";

export default {
  name: "PerfisUsuarioStepPlanos",
  components: {
    DataTable,
    Chip,
    PerfisInfoAlert,
    PerfisBulkSelectActions
  },
  props: {
    idfornecedor: { type: String, required: true },
    idperfilEdicao: { type: String, default: null },
    idformapagtos: { type: Array, default: () => [] }
  },
  data: () => ({
    syncingSelection: false,
    syncTimer: null,
    loadingSelectAll: false,
    ignoreEmptySelectUntil: 0,
    dtLoadingUnwatch: null,
    headers: [
      { text: "Descrição", value: "descricao" },
      { text: "Tipo", value: "tipo", align: "center" },
      {
        text: "ID Externo (Cód ERP)",
        value: "id_formapagto_erp",
        align: "center"
      }
    ],
    filters: [
      {
        label: "Tipo",
        query: "tipo",
        type: "autocomplete",
        options: [],
        clearable: true
      }
    ]
  }),
  computed: {
    selectedCount() {
      return uniqueIds(this.idformapagtos).length;
    },
    endpoint() {
      if (!this.idfornecedor) return "";
      return `/api/v4/perfil-usuario/fornecedor/${this.idfornecedor}/planos-pagamento/datatable`;
    }
  },
  watch: {
    idformapagtos() {
      this.scheduleSelectionSync();
    }
  },
  mounted() {
    this.loadTipoFilterOptions();
    this.bindDatatableLoadingWatch();
  },
  beforeDestroy() {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    if (this.dtLoadingUnwatch) {
      this.dtLoadingUnwatch();
      this.dtLoadingUnwatch = null;
    }
  },
  methods: {
    async loadTipoFilterOptions() {
      if (!this.idfornecedor) return;
      try {
        const data = await this.$store.dispatch(
          PERFIL_USUARIO_PLANOS_LIST,
          this.idfornecedor
        );
        const planos = data?.data || [];
        const tipos = Array.from(
          new Set(planos.map(p => p.tipo).filter(Boolean))
        );
        this.filters = [
          {
            label: "Tipo",
            query: "tipo",
            type: "autocomplete",
            options: tipos.map(t => ({ text: t, value: t })),
            clearable: true
          }
        ];
      } catch (err) {
        this.filters = [
          {
            label: "Tipo",
            query: "tipo",
            type: "autocomplete",
            options: [],
            clearable: true
          }
        ];
      }
    },
    bindDatatableLoadingWatch() {
      this.$nextTick(() => {
        const dt = this.$refs.datatable;
        if (!dt || this.dtLoadingUnwatch) return;
        this.dtLoadingUnwatch = this.$watch(
          () => dt.loading,
          (loading, prev) => {
            if (prev === true && loading === false) {
              this.onDatatablePageLoaded();
            }
          }
        );
      });
    },
    onDatatablePageLoaded() {
      this.ignoreEmptySelectUntil = Date.now() + 1500;
      this.syncingSelection = true;
      this.$nextTick(() => {
        this.syncDatatableSelection();
        this.$nextTick(() => {
          this.syncingSelection = false;
        });
      });
    },
    onFiltersApplied() {
      this.scheduleSelectionSync();
    },
    scheduleSelectionSync() {
      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
      }
      this.syncTimer = setTimeout(() => {
        this.syncDatatableSelection();
      }, 100);
    },
    syncDatatableSelection(selectedIds) {
      const dt = this.$refs.datatable;
      if (!dt || !Array.isArray(dt.rows)) return;

      const ids = new Set(
        uniqueIds(selectedIds !== undefined ? selectedIds : this.idformapagtos)
      );
      const nextSelected = dt.rows.filter(r => ids.has(String(r.idformapagto)));
      const currentIds = uniqueIds(
        (dt.selected || []).map(r => r.idformapagto)
      );
      const nextIds = uniqueIds(nextSelected.map(r => r.idformapagto));
      if (sameIdSet(currentIds, nextIds)) return;

      const ownedLock = !this.syncingSelection;
      if (ownedLock) {
        this.syncingSelection = true;
      }
      dt.selected = nextSelected;
      this.$nextTick(() => {
        if (ownedLock) {
          this.syncingSelection = false;
        }
      });
    },
    onTableSelect(rows) {
      if (this.syncingSelection) return;

      const dt = this.$refs.datatable;
      const fromPage = uniqueIds((rows || []).map(r => r.idformapagto));
      if (
        fromPage.length === 0 &&
        (Date.now() < this.ignoreEmptySelectUntil || (dt && dt.loading))
      ) {
        return;
      }

      const pageRows = (dt && dt.rows) || [];
      const pageRowIds = pageRows.map(r => r.idformapagto);
      const merged = mergeCrossPageSelection(
        this.idformapagtos,
        pageRowIds,
        fromPage
      );
      if (!sameIdSet(merged, this.idformapagtos)) {
        this.$emit("update-planos", merged);
        this.$emit("dirty");
      }
    },
    async selectAll() {
      if (!this.idperfilEdicao || this.loadingSelectAll) return;
      this.loadingSelectAll = true;
      try {
        const filterMap = readDatatableFilterMap(this.$refs.datatable);
        const tipo = filterMap.tipo ?? "";
        const resp = await this.$store.dispatch(
          PERFIL_USUARIO_VINCULAR_TODOS_PLANOS,
          {
            idperfil: this.idperfilEdicao,
            params: { tipo },
            body: { tipo }
          }
        );
        const summary = resp?.data || {};
        const vinculados = summary.vinculados_nesta_operacao ?? 0;
        if (vinculados === 0) {
          this.$vueOnToast.pop(
            "info",
            "Nenhum plano novo vinculado (já vinculados ou filtros vazios)."
          );
        } else {
          this.$vueOnToast.pop(
            "success",
            `${vinculados} plano(s) vinculado(s) ao perfil.`
          );
        }
        this.$emit("vinculos-alterados");
      } catch (err) {
        /* ERRORS_SHOW na action */
      } finally {
        this.loadingSelectAll = false;
      }
    },
    async clearAll() {
      if (!this.idperfilEdicao || this.loadingSelectAll) return;
      this.loadingSelectAll = true;
      try {
        await this.$store.dispatch(
          PERFIL_USUARIO_DESVINCULAR_TODOS_PLANOS,
          this.idperfilEdicao
        );
        this.$vueOnToast.pop("success", "Vínculos de planos removidos.");
        this.$emit("vinculos-alterados");
      } catch (err) {
        /* ERRORS_SHOW na action */
      } finally {
        this.loadingSelectAll = false;
      }
    }
  }
};
</script>

<style scoped>
.perfis-selection-hint {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
</style>
